import { ApiError, type ClientDeps } from './client';
import type { ErrorResponse, GrantRole, PublicMode, SharingResponse } from './types';
import type {
  CatalogueResponse, EvaluationResponse, PublicationPreview, ResourceContentData,
  ResourceResponse, ResourceSummary, ResourceType,
} from '../resources/types';

export interface ResourceClient {
  catalogue(): Promise<CatalogueResponse>;
  types(): Promise<ResourceType[]>;
  resolve(urn: string, publicOnly: boolean): Promise<ResourceResponse>;
  children(id: string, publicOnly: boolean): Promise<ResourceSummary[]>;
  evaluate(id: string, publicOnly: boolean): Promise<EvaluationResponse>;
  create(parentUrn: string, name: string, typeId: string, targetUrn: string | null): Promise<ResourceResponse>;
  save(id: string, expectedVersion: number, content: ResourceContentData): Promise<ResourceResponse>;
  rename(id: string, name: string): Promise<void>;
  move(id: string, parentUrn: string): Promise<void>;
  remove(id: string): Promise<void>;
  restore(id: string): Promise<void>;
  example(): Promise<ResourceResponse>;
  preview(id: string): Promise<PublicationPreview>;
  publish(id: string, mode: 'live' | 'snapshot'): Promise<ResourceResponse>;
  visibility(id: string, mode: PublicMode): Promise<void>;
  sharing(id: string): Promise<SharingResponse>;
  grant(id: string, email: string, role: GrantRole): Promise<void>;
  removeGrant(id: string, email: string): Promise<void>;
}

/** Every mutation returns authoritative server state. Anonymous routes never carry a token. */
export function createResourceClient(deps: ClientDeps): ResourceClient {
  async function request<T>(path: string, method = 'GET', body?: unknown, anonymous = false): Promise<T> {
    const token = anonymous ? null : deps.getToken();
    const headers: Record<string, string> = {};
    if (token !== null) headers['Authorization'] = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const response = await deps.fetch(`${deps.baseUrl}${path}`, {
      method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok) {
      if (response.status === 401 && !anonymous) deps.onUnauthorized();
      throw new ApiError(response.status, await response.json() as ErrorResponse);
    }
    return response.status === 204 ? undefined as T : await response.json() as T;
  }
  const resourcePath = (id: string, anonymous = false) => `${anonymous ? '/public' : ''}/resources/${encodeURIComponent(id)}`;
  return {
    catalogue: () => request('/catalogue'),
    types: () => request('/resource-types'),
    resolve: (urn, publicOnly) => request(`${publicOnly ? '/public' : ''}/resources/resolve?urn=${encodeURIComponent(urn)}`, 'GET', undefined, publicOnly),
    children: (id, publicOnly) => request(`${resourcePath(id, publicOnly)}/children`, 'GET', undefined, publicOnly),
    evaluate: (id, publicOnly) => request(`${resourcePath(id, publicOnly)}/evaluation`, 'GET', undefined, publicOnly),
    create: (parentUrn, name, typeId, targetUrn) => request('/resources', 'POST', {
      parentUrn, name, typeId, targetUrn,
      ...(typeId === 'time-series' ? { series: { unit: '1', frequency: 'quarterly', points: [] } } : {}),
    }),
    save: (id, expectedVersion, content) => request(`${resourcePath(id)}/content`, 'PUT', { expectedVersion, ...content }),
    rename: (id, name) => request(resourcePath(id), 'PATCH', { name }),
    move: (id, parentUrn) => request(`${resourcePath(id)}/move`, 'POST', { parentUrn }),
    remove: (id) => request(resourcePath(id), 'DELETE'),
    restore: (id) => request(`${resourcePath(id)}/restore`, 'POST'),
    example: () => request('/catalogue/example', 'POST'),
    preview: (id) => request(`${resourcePath(id)}/publication-preview`, 'POST'),
    publish: (id, mode) => request(`${resourcePath(id)}/publish`, 'POST', { mode }),
    visibility: (id, mode) => request(`${resourcePath(id)}/visibility`, 'PUT', { mode }),
    sharing: (id) => request(`${resourcePath(id)}/sharing`),
    grant: (id, email, role) => request(`${resourcePath(id)}/grants/${encodeURIComponent(email)}`, 'PUT', { role }),
    removeGrant: (id, email) => request(`${resourcePath(id)}/grants/${encodeURIComponent(email)}`, 'DELETE'),
  };
}
